import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import { CALENDAR_GA_EVENT_ACTION, CALENDAR_GA_EVENT_LABEL } from 'constants/analytics'
import ModalContext from 'contexts/ModalContext'
import { OfferPageActions, generateOfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { getMaxGuestCount } from 'lib/offer/occupancyUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import React, { useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from 'react'
import OfferCapacityWithAgesVerticalSelection from '../../CapacityWithAges/OfferCapacityWithAgesVerticalSelection'
import ModalBase from 'LuxKit/Modal/ModalBase'
import OfferCapacityWithAgesVerticalSidebarSelection from '../../CapacityWithAges/OfferCapacityWithAgesVerticalSidebarSelection'
import OfferCapacityWithAgesSelection from '../../CapacityWithAges/OfferCapacityWithAgesSelection'
import OfferCalendarCapacityChip from './OfferCalendarCapacityChip'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalHeader from 'LuxKit/Modal/ModalHeader'

interface Props {
  offer: App.Offer | App.BedbankOffer | App.Tours.TourV2Offer
  rooms?: Array<App.Occupants>
  open: boolean
  disableMultiRoom?: boolean
  capacities?: Array<App.RoomCapacity>
  occupantUnit?: 'guest' | 'traveller'
  variant?: 'modal' | 'dropdown'
}
const defaultRooms = [generateOfferPageRoom()]

function OfferCalendarCapacitySelect(props: Props) {
  const {
    offer,
    open,
    disableMultiRoom,
    capacities,
    occupantUnit = 'guest',
    rooms = defaultRooms,
    variant = 'dropdown',
  } = props
  const triggerRef = useRef<HTMLButtonElement>(null)
  const isMobile = useScreenSizeOnly('mobile')
  const showModal = useContext(ModalContext)
  const [localRooms, setLocalRooms] = useState<Array<App.RoomOccupants | App.Occupants>>(rooms)

  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)
  const dispatch = useOfferPageDispatch()

  const saveRooms = useCallback(() => {
    dispatch({
      type: OfferPageActions.setRooms,
      rooms: localRooms as any,
    })
  }, [dispatch, localRooms])

  const onToggleOpen = useCallback(() => {
    dispatch({ type: OfferPageActions.toggleSubFilter, filter: 'capacity' })
    if (open) {
      onUserAnalyticsAction(CALENDAR_GA_EVENT_ACTION.CloseSubFilter, CALENDAR_GA_EVENT_LABEL.OpenCapacity)
    } else {
      onUserAnalyticsAction(CALENDAR_GA_EVENT_ACTION.OpenSubFilter, CALENDAR_GA_EVENT_LABEL.OpenCapacity)
    }
  }, [dispatch, onUserAnalyticsAction, open])

  const onClose = useCallback(() => {
    saveRooms()
    dispatch({ type: OfferPageActions.closeSubFilter })
    onUserAnalyticsAction(CALENDAR_GA_EVENT_ACTION.CloseSubFilter, CALENDAR_GA_EVENT_LABEL.OpenCapacity)
  }, [dispatch, onUserAnalyticsAction, saveRooms])

  const maxGuests = useMemo((): number => getMaxGuestCount(capacities), [capacities])

  useEffect(() => {
    if (open && variant === 'modal') {
      if (disableMultiRoom) {
        showModal(<ModalBase height="max">
          <OfferCapacityWithAgesVerticalSidebarSelection
            footerText="Apply"
            disableMultiRoom
          />
        </ModalBase>,
        ).then(() => {
          onClose()
        })
      } else {
        showModal(<ModalBase height="max">
          <ModalHeader title="Who is going?" />
          <ModalBody>
            <OfferCapacityWithAgesSelection capacities={capacities} />
          </ModalBody>
          <ModalFooter
            primaryActionProps={{
              children: 'Apply',
              onClick: (e, resolve) => resolve(),
            }}
          />
        </ModalBase>).then(() => {
          dispatch({ type: OfferPageActions.closeSubFilter })
        })
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const onDropdownSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    saveRooms()
    onClose()
  }, [onClose, saveRooms])

  const formId = useId()

  return (<>
    <OfferCalendarCapacityChip
      selected={open}
      ref={triggerRef}
      onClick={onToggleOpen}
      offer={offer}
      disableMultiRoom={disableMultiRoom}
    />
    {variant === 'dropdown' && <DropdownSheet
      size="M"
      open={open && !isMobile}
      hasDismissButton
      onClose={onClose}
      anchorRef={triggerRef}
      triggerRef={triggerRef}
      data-testid="offer-calendar-capacity-select-dropdown"
      title="Who is going?"
      subtitle={maxGuests > 0 ? `Maximum occupancy is ${pluralizeToString(occupantUnit, maxGuests)}` : undefined}
      primaryActionProps={{
        children: 'Apply',
        onClick: onClose,
      }}
    >
      <form onSubmit={onDropdownSubmit} id={formId}>
        <OfferCapacityWithAgesVerticalSelection
          capacities={capacities}
          disableMultiRoom={disableMultiRoom}
          onChange={setLocalRooms}
        />
      </form>
    </DropdownSheet>}
  </>
  )
}

export default withOfferPageState((state) => {
  return {
    offer: state.offer ?? state.bedbankOffer ?? state.tourV2Offer,
    rooms: state.rooms,
    open: state.activeSubFilter === 'capacity',
  }
}, OfferCalendarCapacitySelect)
